import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowRight, Check, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';

const PartnerRegistrierenPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [checked, setChecked] = useState(false); // NEW: deterministic flag
  const [partnerId, setPartnerId] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false); // NEW

  const checkExistingPartner = async (checkEmail) => {
    if (!checkEmail || !checkEmail.includes('@')) return { isPaid: false, needsSetup: false };
    
    setChecking(true);
    let isPaid = false;
    let needsSetupFlag = false;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/check-partner-email?email=${encodeURIComponent(checkEmail)}`);
      if (response.ok) {
        const data = await response.json();
        isPaid = data.exists && data.paid;
        needsSetupFlag = data.needs_setup || false;
        
        setAlreadyPaid(isPaid);
        setNeedsSetup(needsSetupFlag);
        setPartnerId(data.partner_id || '');
        
        if (needsSetupFlag && data.partner_id) {
          setMagicLink(`/partner/set-password?token=${data.invite_token || ''}`);
        }
      }
    } catch (err) {
      // Silent fail
    } finally {
      setChecking(false);
      setChecked(true); // Mark as checked
    }
    
    return { isPaid, needsSetup: needsSetupFlag };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setChecked(false); // Reset check state
    
    // Check if already paid - wait for result
    const isPaid = await checkExistingPartner(email);
    
    setLoading(false);
    
    // If already paid, don't proceed
    if (isPaid) {
      return; // UI will show "already paid" message
    }
    
    // Proceed with checkout
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Checkout fehlgeschlagen');
      }

      // Redirect to Stripe
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}${magicLink}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Partner Registrierung | Dein Gutachten Direkt</title>
        <meta name="description" content="Registrieren Sie sich als Partner und profitieren Sie von unserem Gutachten-Netzwerk." />
      </Helmet>
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          {!success ? (
            <>
              <div className="text-center mb-12">
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                  Partner werden
                </h1>
                <p className="text-xl text-gray-600">
                  Starten Sie jetzt mit Ihrer Registrierung
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-3xl p-10 lg:p-12 shadow-lg">
                {alreadyPaid ? (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-10 h-10 text-brand-green" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Du hast bereits bezahlt!</h2>
                    <p className="text-gray-600 mb-6">
                      Dein Account existiert bereits. Bitte logge dich ein oder richte deinen Zugang über den Link in deiner Email ein.
                    </p>
                    <div className="flex flex-col gap-3">
                      <a
                        href="/portal/login"
                        className="px-6 py-3 bg-brand-green hover:bg-brand-green-dark text-white font-semibold rounded-xl transition-colors inline-block"
                      >
                        Zum Login
                      </a>
                      <p className="text-sm text-gray-500">
                        Noch keine Email erhalten?{' '}
                        <a href="/kontakt" className="text-brand-green hover:text-brand-green-dark font-semibold underline">
                          Support kontaktieren
                        </a>
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-3">
                      E-Mail-Adresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent text-gray-900"
                      placeholder="ihre.email@firma.de"
                    />
                  </div>

                  <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-6">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <strong>Abo:</strong> ab 29,99€ inkl. MwSt. pro Monat. Nach Registrierung erhalten Sie eine E-Mail zur Bestätigung und zum Onboarding.
                    </p>
                  </div>

                  {checked && alreadyPaid && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                      <h3 className="font-semibold text-blue-900 mb-2">Sie haben bereits ein aktives Abo</h3>
                      <p className="text-sm text-blue-700 mb-4">
                        Ihre E-Mail-Adresse ist bereits mit einem aktiven Partner-Abo verbunden.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        {needsSetup && magicLink ? (
                          <a href={magicLink} className="flex-1">
                            <Button className="w-full bg-brand-green hover:bg-brand-green-dark text-white">
                              Zugang jetzt einrichten
                            </Button>
                          </a>
                        ) : (
                          <a href="/portal/login" className="flex-1">
                            <Button className="w-full bg-brand-green hover:bg-brand-green-dark text-white">
                              Zum Login
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || checking || (checked && alreadyPaid)}
                    size="lg"
                    className="w-full bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-7 rounded-2xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checking ? 'Prüfe...' : loading ? 'Wird registriert...' : (checked && alreadyPaid) ? 'Bereits registriert' : 'Jetzt registrieren'}
                    {!loading && !checking && !alreadyPaid && <ArrowRight className="w-5 h-5 ml-3" />}
                  </Button>
                </form>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-12">
                <div className="w-20 h-20 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-brand-green/15">
                  <Check className="w-10 h-10 text-brand-green" />
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                  Registrierung erfolgreich!
                </h1>
                <p className="text-xl text-gray-600">
                  Bitte bestätigen Sie Ihre E-Mail-Adresse
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-3xl p-10 lg:p-12 shadow-lg">
                <div className="mb-8">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    <strong>MVP-Modus:</strong> Normalerweise würden Sie jetzt eine E-Mail erhalten. Im MVP zeigen wir Ihnen den Verifizierungs-Link direkt hier:
                  </p>
                  
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <p className="text-sm text-gray-600 mb-3">Verifizierungs-Link:</p>
                    <div className="flex items-center gap-3">
                      <code className="flex-1 bg-gray-100 px-4 py-3 rounded-lg text-sm font-mono text-gray-900 break-all">
                        {window.location.origin}{magicLink}
                      </code>
                      <Button
                        onClick={copyLink}
                        variant="outline"
                        className="flex-shrink-0"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <a href={magicLink}>
                  <Button size="lg" className="w-full bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-2xl font-semibold">
                    E-Mail jetzt bestätigen
                    <ArrowRight className="w-5 h-5 ml-3" />
                  </Button>
                </a>
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PartnerRegistrierenPage;